<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n';
import { useRouter } from '#imports';
import type { Music } from '~/services/musicService'
import SongRow from './SongRow.vue'
import { rowTags, lyricsOnlyMatch } from './rowPresentation.mjs'
import { rankDelta } from './listeningPresentation.mjs'
import { visiblePlays, sourceLabels } from './discovery-source.mjs'
import SongCard from './SongCard.vue'
import MmButton from '../base/MmButton.vue'
import MmIcon from '../base/MmIcon.vue'
import SongMenu from './SongMenu.vue'
import { usePlayerStore } from '~/stores/player'
import { useMusicCreate } from '~/composables/useMusicCreate'
import { useToast } from '~/composables/useToast'
import { trackEvent } from '~/utils/analytics'
import { sendSongEvent, type PlaybackSurface } from '~/services/songPlayback'
import { createGrowthEventId } from '~/utils/growthEvents'
import ListExposure from './ListExposure.vue'
import type { ListSong } from './rankings'
import { isCompetitionEntry } from './competition.mjs'

// 获取国际化函数
const { t } = useI18n()

// 使用音乐交互的composable
const useMusicInteraction = () => {
  const router = useRouter()
  const toast = useToast()
  const { saveMusicData } = useMusicCreate()
  const { t } = useI18n()

  const player = usePlayerStore()
  const togglePlayState = (music: Music) => {
    const sameQueue = props.musicList.length === player.queue.length && props.musicList.every((song, index) => song.id === player.queue[index]?.id)
    if (player.currentTrack?.id === music.id && sameQueue) player.toggle()
    else player.play(music, contextFor(music), { queue: props.musicList })
  }
  const isPlaying = (music: Music) => player.currentTrack?.id === music.id && player.playing
  const isAudioLoading = (music: Music) => player.currentTrack?.id === music.id && player.loading

  // 创建同款音乐
  const createSimilarMusic = (music: Music) => {
    if (!music) return
    try {
      // 从音乐元数据中提取歌词信息或使用描述作为歌词
      const lyricsContent = music.metadata?.prompt || ''
      const musicTitle = music.title || ''
      // 确保标签是字符串格式
      const tagsString = typeof music.metadata?.tags === 'string' ?
                        music.metadata.tags :
                        (music.metadata?.tags ? String(music.metadata.tags) : '')

      // 准备做同款数据
      const similarData = {
        prompt: music.metadata?.prompt || '',
        tags: tagsString,
        instrumental: false,
        lyrics: lyricsContent,
        title: musicTitle,
        mode: 'advanced'
      }

      // 保存音乐数据并导航
      saveMusicData(similarData, router.currentRoute.value.path)
      router.push('/create')
    } catch (error) {
      toast.error(t('common.operation_failed'))
    }
  }

  return { togglePlayState, isPlaying, isAudioLoading, createSimilarMusic }
}

const props = defineProps<{
  musicList: Array<Music & { cover_hue?: number }>
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  showCreateSimilar?: boolean
  autoLoadMore?: boolean
  source?: string
  errorTitle?: string
  errorDescription?: string
  emptyDescription?: string
  variant?: 'collection' | 'grid' | 'search'
  searchQuery?: string
  rankOffset?: number
  hideRanks?: boolean
  rankingKey?: string
  competitionOpen?: boolean
}>()

const canRemix = (music: ListSong) => props.showCreateSimilar && music.remix_allowed !== false
const fallbackRequestId = ref(createGrowthEventId())
watch(() => props.musicList, () => { fallbackRequestId.value = createGrowthEventId() })
const contextFor = (music: ListSong) => ({
  ...music.listContext,
  requestId: music.listContext?.requestId || fallbackRequestId.value,
  surface: (props.source === 'music_search' || props.variant === 'search' ? 'search' : props.source === 'home' ? 'home' : props.source === 'chart' ? 'chart' : 'music_ranking') as PlaybackSurface,
  position: music.listContext?.position || props.musicList.findIndex(song => song.id === music.id) + 1,
})

const emit = defineEmits<{
  (e: 'reload'): void
  (e: 'loadMore'): void
}>()

// 使用音乐交互逻辑
const {
  isPlaying,
  isAudioLoading,
  togglePlayState,
  createSimilarMusic
} = useMusicInteraction()

const getMusicTrackParams = (music: Music, actionArea: string) => ({
  source: props.source || 'music_list',
  action_area: actionArea,
  song_id: music.id,
  title: music.title || '',
  has_audio: Boolean(music.audio_url),
  has_image: Boolean(music.image_url)
})

const trackMusicCardClick = (music: Music, actionArea: string) => {
  trackEvent('music_card_click', getMusicTrackParams(music, actionArea))
  void sendSongEvent(music.id, 'click', contextFor(music))
}

const handleTogglePlayState = (music: Music) => {
  void sendSongEvent(music.id, 'click', contextFor(music))
  trackEvent('music_play_click', {
    ...getMusicTrackParams(music, 'play_button'),
    will_play: !isPlaying(music)
  })
  togglePlayState(music)
}

const handleCreateSimilarMusic = (music: Music) => {
  trackEvent('create_similar_click', getMusicTrackParams(music, 'create_similar'))
  createSimilarMusic(music)
}

// 加载更多触发器
const loadMoreTrigger = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreCooldown = ref(false)

// 处理加载更多
const handleLoadMore = () => {
  if (!props.error && !props.isLoading && !props.isLoadingMore && props.hasMore && !loadMoreCooldown.value) {
    loadMoreCooldown.value = true
    emit('loadMore')
    setTimeout(() => { loadMoreCooldown.value = false }, 1000)
  }
}

// 重新加载
const reload = () => {
  trackEvent('music_list_reload_click', {
    source: props.source || 'music_list'
  })
  emit('reload')
}

onMounted(() => {


  // 使用 IntersectionObserver 监听加载更多触发器
  // 仅在 autoLoadMore !== false 时设置 IntersectionObserver
  if (props.autoLoadMore !== false) {
    nextTick(() => {
      if (loadMoreTrigger.value) {
        observer.value = new IntersectionObserver((entries) => {
          if (props.error || props.isLoading || props.isLoadingMore || !props.hasMore || props.musicList.length === 0 || loadMoreCooldown.value) {
            return
          }
          const entry = entries[0]
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        }, {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        })
        observer.value.observe(loadMoreTrigger.value)
      }
    })

    nextTick(() => {
      const documentHeight = document.documentElement.scrollHeight
      const windowHeight = window.innerHeight
      if (!props.error && documentHeight <= windowHeight && props.hasMore && !props.isLoading && !props.isLoadingMore) {
        handleLoadMore()
      }
    })
  }
})

// 组件卸载时清理
onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})

// 监听props变化，重新设置IntersectionObserver（仅 autoLoadMore 模式）
watch(() => [props.musicList.length, props.hasMore], () => {
  if (props.autoLoadMore === false) return

  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }

  nextTick(() => {
    if (loadMoreTrigger.value && props.hasMore) {
      observer.value = new IntersectionObserver((entries) => {
        if (props.error || props.isLoading || props.isLoadingMore || !props.hasMore || props.musicList.length === 0 || loadMoreCooldown.value) {
          return
        }
        const entry = entries[0]
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      }, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      })
      observer.value.observe(loadMoreTrigger.value)
    }
  })
})

// 格式化时长
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
</script>

<template>
  <div class="collection-songs">
    <div v-if="isLoading" role="status"><slot name="loading"><p class="song-feedback">{{ t('music.loading') }}</p></slot></div>
    <div v-else-if="musicList.length" :class="variant === 'collection' ? 'song-rows' : 'grid-cards'">
      <ListExposure v-for="(music, index) in musicList" :key="music.id" :id="music.id" :context="contextFor(music)" class="collection-song" :class="{ 'collection-song-row': variant === 'collection' }">
        <SongRow v-if="variant === 'collection'" :competition-rank="competitionOpen && isCompetitionEntry(rankingKey, music)" compact inline-play :title="music.title || t('copyright.untitled')" :artist="(music as ListSong).creator?.display_name || music.display_name || ''" :src="music.image_url" :hue="music.cover_hue" :rank="hideRanks || variant === 'search' ? undefined : (music as ListSong).rank ?? (rankOffset || 0) + index + 1" :tags="rowTags(music.metadata?.tags)" :lyric-match="variant === 'search' ? lyricsOnlyMatch(music, searchQuery) : null" :plays="visiblePlays(music)" :source-labels="sourceLabels(music, rankingKey)" :rank-delta="rankDelta(music)" :is-new="(music as ListSong).is_new" :duration="music.metadata?.duration" :to="`/music/song/${music.id}`" :state="isAudioLoading(music) ? 'loading' : isPlaying(music) ? 'playing' : 'idle'" :show-more="false" cancelable @play="handleTogglePlayState(music)" @open="trackMusicCardClick(music, 'text')">
          <template #actions>
            <SongMenu inline-actions :song="music" :show-remix="canRemix(music)" @remix="handleCreateSimilarMusic" />
          </template>
        </SongRow>
        <SongCard v-else :plays="visiblePlays(music)" :source-labels="sourceLabels(music, rankingKey)" :title="music.title || music.metadata?.prompt || t('copyright.untitled')" :artist="(music as ListSong).creator?.display_name || music.display_name || ''" :src="music.image_url" :hue="music.cover_hue" :to="`/music/song/${music.id}`" :state="isAudioLoading(music) ? 'loading' : isPlaying(music) ? 'playing' : 'idle'" cancelable @play="handleTogglePlayState(music)" @open="trackMusicCardClick(music, 'cover')" />
        <div v-if="variant !== 'collection' && variant !== 'search'" class="song-extras"><p v-if="music.metadata?.tags" class="song-tags">{{ music.metadata.tags }}</p><button v-if="canRemix(music)" type="button" class="similar" :aria-label="t('music.createSimilar') + ': ' + (music.title || music.metadata?.prompt || '')" @click="handleCreateSimilarMusic(music)"><MmIcon name="plus" :size="16" /><span class="similar-label">{{ t('music.createSimilar') }}</span></button></div>
      </ListExposure>
    </div>
    <template v-else-if="!error"><slot name="empty"><div class="song-feedback" role="status"><h2>{{ t('music.noResults') }}</h2><p v-if="emptyDescription">{{ emptyDescription }}</p></div></slot></template>
    <div v-if="musicList.length && autoLoadMore !== false && (hasMore || isLoadingMore || error)" v-show="!isLoading" ref="loadMoreTrigger" class="song-load-more">
      <div v-if="error" class="song-feedback" role="alert"><p>{{ errorTitle || error }}</p><p v-if="errorDescription">{{ errorDescription }}</p><MmButton variant="secondary" :disabled="isLoading || isLoadingMore" @click="reload">{{ t('ux.retry') }}</MmButton></div>
      <p v-else-if="isLoadingMore" role="status">{{ t('music.loading') }}</p><MmButton v-else-if="hasMore" variant="secondary" @click="handleLoadMore">{{ t('common.loadMore') }}</MmButton>
    </div>
    <template v-if="!isLoading && error && (!musicList.length || autoLoadMore === false)"><slot name="error" :retry="reload"><div class="song-feedback" role="alert"><h2>{{ errorTitle || error }}</h2><p v-if="errorDescription">{{ errorDescription }}</p><MmButton variant="secondary" :disabled="isLoading" @click="reload">{{ t('ux.retry') }}</MmButton></div></slot></template>
  </div>
</template>

<style scoped>
.similar:hover { color: var(--text); }
.collection-songs, .collection-song { min-width: 0; }
.song-rows { display: grid; }
.collection-song-row { min-width: 0; }
.song-extras { display: flex; flex-wrap: wrap; gap: var(--s-2); align-items: center; margin-top: var(--s-2); }
.collection-song-row .song-extras { margin: 0; }

@media(max-width:767px) { .collection-song-row .similar { width: 44px; justify-content: center;  text-decoration: underline; text-underline-offset: var(--s-1); } .collection-song-row .similar-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; } }
.song-tags { color: var(--text-2); font-size: var(--t-micro); line-height: 1.5; overflow-wrap: anywhere; flex: 1 1 120px; }
.similar { display: inline-flex; align-items: center; gap: var(--s-2); min-height: 44px; padding: var(--s-2); border-radius: var(--r-pill); color: var(--text-2); font-size: var(--t-micro); text-align: start;  text-decoration: underline; text-underline-offset: var(--s-1); }
.similar:hover { background: var(--surface-2); }
.song-feedback { display: grid; justify-items: center; gap: var(--s-3); padding: var(--s-8) 0; color: var(--text-2); text-align: center; }
.song-feedback > :deep(.btn) { justify-self: center; }
.song-feedback h2 { color: var(--text); font-size: var(--t-h3); }
.song-feedback p { margin: 0; }
.song-load-more { min-height: 100px; display: grid; place-items: center; padding: var(--s-8) 0; color: var(--text-2); }
.grid-cards { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 28px 18px; }
@media(min-width:640px) { .grid-cards { grid-template-columns: repeat(3,minmax(0,1fr)); } }
@media(min-width:1024px) { .grid-cards { grid-template-columns: repeat(5,minmax(0,1fr)); gap: 32px 24px; } }
</style>
